import { MediaObject } from 'types'
import validators from './base'
import { ScheduledPostSettings, TikTokPostSettings } from 'api/clients/ScheduledPostsClient'

// Source: https://developers.tiktok.com/doc/content-posting-api-media-transfer-guide#video_restrictions
// Source: https://developers.tiktok.com/doc/content-posting-api-media-transfer-guide#image_restrictions

const tiktokContentValidator = (content: string): string[] => {
  const rules = {
    maxCharacters: 2200,
  }

  let errors = validators.validateTextLength(content, rules.maxCharacters, false)
  return errors
}

const tiktokFileValidator = (files: MediaObject[]): string[] => {
  const rules = {
    minVideos: 1,
    maxVideos: 1,
  }

  return validators.validateFileCount(files, rules.minVideos, rules.maxVideos)
}

const tiktokVideosSizeValidator = (files: MediaObject[]): string[] => {
  const rules = {
    maxSize: 4 * 1024 * 1024 * 1024, // 4GB
  }

  return validators.validateVideosSize(files, rules.maxSize, 'GB')
}

const tiktokImageSizeValidator = (files: MediaObject[]): string[] => {
  return []
}

const tiktokDurationValidator = (files: MediaObject[]): string[] => {
  const rules = {
    minDuration: 3,
    maxDuration: 180, // 3 minutes
  }

  return validators.validateVideosDuration(files, rules.minDuration, rules.maxDuration)
}

const tiktokAspectRatioValidator = (files: MediaObject[]): string[] => {
  const rules = {
    minAspectRatio: 0.53,
    maxAspectRatio: 1.77,
  }

  const labels = {
    minAspectRatio: '9:16',
    maxAspectRatio: '16:9',
  }

  return validators.validateVideosAspectRatio(
    files,
    rules.minAspectRatio,
    rules.maxAspectRatio,
    labels.minAspectRatio,
    labels.maxAspectRatio,
  )
}

const tiktokFormatValidator = (files: MediaObject[]): string[] => {
  const formats = ['video/mp4', 'video/quicktime']

  return validators.validateVideosFormat(files, formats, 'TikTok')
}

const tiktokVideosCountValidator = (files: MediaObject[]): string[] => {
  return validators.validateVideosCount(files, 1, 1)
}

export const isTikTokSettings = (settings?: ScheduledPostSettings | null): settings is TikTokPostSettings => {
  return settings !== undefined && settings !== null && 'privacy_level' in settings
}

const tiktokPostSettingsValidator = (settings?: ScheduledPostSettings): string[] => {
  const errors: string[] = []

  // Don't validate if settings are undefined or null - this means they're being initialized
  if (!settings) {
    return []
  }

  if (!isTikTokSettings(settings)) {
    errors.push('Please indicate the privacy level of your post.')
    return errors
  }

  if (!settings?.privacy_level) {
    errors.push('Please indicate the privacy level of your post.')
  }

  if (!!settings?.disclose_content && !settings.brand_content_toggle && !settings.brand_organic_toggle) {
    errors.push('You need to indicate if your content promotes yourself, a third party, or both.')
  }

  return errors
}

const tiktokValidators = {
  validateContent: tiktokContentValidator,
  validateFiles: tiktokFileValidator,
  validateVideosSize: tiktokVideosSizeValidator,
  validateImagesSize: tiktokImageSizeValidator,
  validateVideosDuration: tiktokDurationValidator,
  validateVideosAspectRatio: tiktokAspectRatioValidator,
  validateVideosFormat: tiktokFormatValidator,
  validateVideosCount: tiktokVideosCountValidator,
  validatePostSettings: tiktokPostSettingsValidator,
}

export default tiktokValidators
